import { useState, useRef } from "react"
import PropTypes from "prop-types"
import { Button } from "react-bootstrap"
import LoadingProgress from "../progressCount/LoadProgress"

/**
 * Hook customizado para gerenciar operações assíncronas
 * @returns {Object} Objeto contendo estado de loading e função de execução
 * @property {boolean} loading - Estado do carregamento
 * @property {Function} execute - Função para executar ações assíncronas
 */
const useAsyncAction = () => {
	const [loading, setLoading] = useState(false)
	const isMounted = useRef(true)
	const isProcessing = useRef(false)

	const execute = async (action) => {
		if (typeof action !== "function") {
			throw new Error("Ação inválida: não é uma função")
		}
		if (!action || isProcessing.current) return

		isProcessing.current = true
		setLoading(true)

		try {
			await action()
		} catch (error) {
			console.error("Async operation failed:", error)
		} finally {
			if (isMounted.current) {
				setLoading(false)
				isProcessing.current = false
			}
		}
	}

	return { loading, execute }
}
/**
 * Componente de botão para operações assíncronas com tratamento de estado
 * @param {Object} props - Propriedades do componente
 * @param {Function} props.onClick - Função assíncrona a ser executada
 * @param {React.ReactNode} props.children - Conteúdo do botão
 * @param {React.ReactNode} [props.loadingComponent] - Componente de loading customizado
 * @param {Object} [props.buttonProps] - Props adicionais para o botão
 * @returns {JSX.Element} Elemento React do botão assíncrono
 */
const AsyncButton = ({
	onClick,
	children,
	loadingComponent = <LoadingProgress />,
	buttonProps,
}) => {
	const { loading, execute } = useAsyncAction()

	return (
		<Button
			{...buttonProps}
			onClick={() => execute(onClick)}
			disabled={loading || buttonProps.disabled}
		>
			{loading ? loadingComponent : children}
		</Button>
	)
}

AsyncButton.propTypes = {
	onClick: PropTypes.func.isRequired,
	children: PropTypes.node.isRequired,
	loadingComponent: PropTypes.node,
	buttonProps: PropTypes.object,
}

export default AsyncButton
